import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { ThemeProvider } from "@/contexts/ThemeContext";

import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AppLayout from "@/components/AppLayout";
import DashboardPage from "@/pages/DashboardPage";
import SchoolsPage from "@/pages/SchoolsPage";
import GradesPage from "@/pages/GradesPage";
import ClassesPage from "@/pages/ClassesPage";
import SubjectsPage from "@/pages/SubjectsPage";
import StudentsPage from "@/pages/StudentsPage";
import StudentProfilePage from "@/pages/StudentProfilePage";
import TeachersPage from "@/pages/TeachersPage";
import TeacherProfilePage from "@/pages/TeacherProfilePage";
import TimetablePage from "@/pages/TimetablePage";
import AttendancePage from "@/pages/AttendancePage";
import GradesEntryPage from "@/pages/GradesEntryPage";
import ReportCardPage from "@/pages/ReportCardPage";
import BehaviorPage from "@/pages/BehaviorPage";
import FeesPage from "@/pages/FeesPage";
import MessagesPage from "@/pages/MessagesPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import HomeworkPage from "@/pages/HomeworkPage";
import QuizzesPage from "@/pages/QuizzesPage";
import ReportsPage from "@/pages/ReportsPage";
import AiAssistantPage from "@/pages/AiAssistantPage";
import NotificationsPage from "@/pages/NotificationsPage";
import SettingsPage from "@/pages/SettingsPage";
import VehiclesPage from "@/pages/transport/VehiclesPage";
import DriversPage from "@/pages/transport/DriversPage";
import RoutesPage from "@/pages/transport/RoutesPage";
import TransportAssignmentsPage from "@/pages/transport/TransportAssignmentsPage";
import TripsPage from "@/pages/transport/TripsPage";
import TransportReportsPage from "@/pages/transport/TransportReportsPage";
import MyRoutePage from "@/pages/transport/MyRoutePage";
import ParentTripPage from "@/pages/transport/ParentTripPage";
import CanteenWalletPage from "@/pages/canteen/CanteenWalletPage";
import StudentWalletPage from "@/pages/canteen/StudentWalletPage";
import HelpPage from "@/pages/HelpPage";
import CanteenPOSPage from "@/pages/canteen/CanteenPOSPage";
import CanteenAdminPage from "@/pages/canteen/CanteenAdminPage";
import HrDashboardPage from "@/pages/hr/HrDashboardPage";
import EmployeesPage from "@/pages/hr/EmployeesPage";
import DepartmentsPage from "@/pages/hr/DepartmentsPage";
import EmployeeDetailPage from "@/pages/hr/EmployeeDetailPage";
import LeavesPage from "@/pages/hr/LeavesPage";
import PayrollPage from "@/pages/hr/PayrollPage";
import PayrollReportPage from "@/pages/hr/PayrollReportPage";
import AttendanceTodayPage from "@/pages/hr/AttendanceTodayPage";
import RecruitmentPage from "@/pages/hr/RecruitmentPage";
import UsersPage from "@/pages/hr/UsersPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import ProfilePage from "@/pages/ProfilePage";
import BulkImportPage from "@/pages/BulkImportPage";
import ParentsPage from "@/pages/ParentsPage";
import ParentProfilePage from "@/pages/ParentProfilePage";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">...</div>;
  if (user === false) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  return <AppLayout>{children}</AppLayout>;
}

// Exposes window.handlePushDeepLink(url) so the native WebView wrapper can deep-link
// the user into a specific app page when they tap a notification.
function PushDeepLinkBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    window.handlePushDeepLink = (target) => {
      if (typeof target !== "string" || !target) return;
      try {
        // Accept either absolute or relative; we only honor the path/search portion.
        const u = new URL(target, window.location.origin);
        navigate(u.pathname + u.search);
      } catch {
        // Fall back to direct nav
        navigate(target);
      }
    };
    return () => { try { delete window.handlePushDeepLink; } catch {} };
  }, [navigate]);
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
      <I18nProvider>
        <ThemeProvider>
          <AuthProvider>
            <BrowserRouter>
              <ScrollToTop />
              <PushDeepLinkBridge />
              <Toaster richColors position="top-center" />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/schools" element={<Protected><SchoolsPage /></Protected>} />
            <Route path="/grades" element={<Protected><GradesPage /></Protected>} />
            <Route path="/classes" element={<Protected><ClassesPage /></Protected>} />
            <Route path="/subjects" element={<Protected><SubjectsPage /></Protected>} />
            <Route path="/students" element={<Protected><StudentsPage /></Protected>} />
            <Route path="/students/:id" element={<Protected><StudentProfilePage /></Protected>} />
            <Route path="/teachers" element={<Protected><TeachersPage /></Protected>} />
            <Route path="/teachers/:id" element={<Protected><TeacherProfilePage /></Protected>} />
            <Route path="/timetable" element={<Protected><TimetablePage /></Protected>} />
            <Route path="/attendance" element={<Protected><AttendancePage /></Protected>} />
            <Route path="/grades-entry" element={<Protected><GradesEntryPage /></Protected>} />
            <Route path="/report-card" element={<Protected><ReportCardPage /></Protected>} />
            <Route path="/behavior" element={<Protected><BehaviorPage /></Protected>} />
            <Route path="/fees" element={<Protected><FeesPage /></Protected>} />
            <Route path="/messages" element={<Protected><MessagesPage /></Protected>} />
            <Route path="/announcements" element={<Protected><AnnouncementsPage /></Protected>} />
            <Route path="/homework" element={<Protected><HomeworkPage /></Protected>} />
            <Route path="/quizzes" element={<Protected><QuizzesPage /></Protected>} />
            <Route path="/reports" element={<Protected><ReportsPage /></Protected>} />
            <Route path="/ai" element={<Protected><AiAssistantPage /></Protected>} />
            <Route path="/notifications" element={<Protected><NotificationsPage /></Protected>} />
            <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
            <Route path="/transport/vehicles" element={<Protected><VehiclesPage /></Protected>} />
            <Route path="/transport/drivers" element={<Protected><DriversPage /></Protected>} />
            <Route path="/transport/routes" element={<Protected><RoutesPage /></Protected>} />
            <Route path="/transport/assignments" element={<Protected><TransportAssignmentsPage /></Protected>} />
            <Route path="/transport/trips" element={<Protected><TripsPage /></Protected>} />
            <Route path="/transport/reports" element={<Protected><TransportReportsPage /></Protected>} />
            <Route path="/canteen/wallet" element={<Protected><CanteenWalletPage /></Protected>} />
            <Route path="/canteen/my-wallet" element={<Protected><StudentWalletPage /></Protected>} />
            <Route path="/help" element={<Protected><HelpPage /></Protected>} />
            <Route path="/canteen/pos" element={<Protected><CanteenPOSPage /></Protected>} />
            <Route path="/canteen" element={<Protected><CanteenAdminPage /></Protected>} />
            <Route path="/my-route" element={<Protected><MyRoutePage /></Protected>} />
            <Route path="/parent/trip/:tripId" element={<Protected><ParentTripPage /></Protected>} />
            <Route path="/hr" element={<Protected><HrDashboardPage /></Protected>} />
            <Route path="/hr/employees" element={<Protected><EmployeesPage /></Protected>} />
            <Route path="/hr/departments" element={<Protected><DepartmentsPage /></Protected>} />
            <Route path="/hr/employees/:eid" element={<Protected><EmployeeDetailPage /></Protected>} />
            <Route path="/hr/leaves" element={<Protected><LeavesPage /></Protected>} />
            <Route path="/hr/payroll" element={<Protected><PayrollPage /></Protected>} />
            <Route path="/hr/payroll-report" element={<Protected><PayrollReportPage /></Protected>} />
            <Route path="/hr/attendance-today" element={<Protected><AttendanceTodayPage /></Protected>} />
            <Route path="/hr/recruitment" element={<Protected><RecruitmentPage /></Protected>} />
            <Route path="/hr/users" element={<Protected><UsersPage /></Protected>} />
            <Route path="/audit-logs" element={<Protected><AuditLogsPage /></Protected>} />
            <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
            <Route path="/import" element={<Navigate to="/import/students" replace />} />
            <Route path="/import/teachers" element={<Navigate to="/import/employees" replace />} />
            <Route path="/import/:type" element={<Protected><BulkImportPage /></Protected>} />
            <Route path="/students/import" element={<Navigate to="/import/students" replace />} />
            <Route path="/parents" element={<Protected><ParentsPage /></Protected>} />
            <Route path="/parents/:id" element={<Protected><ParentProfilePage /></Protected>} />
            {/* Compatibility redirects (preserve old / sidebar URLs) */}
            <Route path="/users" element={<Navigate to="/hr/users" replace />} />
            <Route path="/finance" element={<Navigate to="/fees" replace />} />
            <Route path="/finance/fees" element={<Navigate to="/fees" replace />} />
            <Route path="/finance/invoices" element={<Navigate to="/fees" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
    </ErrorBoundary>
  );
}
